import { useCallback, useMemo, useState } from "react";
import useStationSearch from "../hooks/useStationSearch";
import {
    Container,
    Box,
    Button,
    Stack,
    Typography,
    IconButton,
    CircularProgress,
    Chip,
    TextField,
    MenuItem,
    Paper,
    Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import TripResultsList from "../components/TripResultsList";
import DateStrip from "../components/DateStrip";
import { searchTripsApi } from "../api/trips";

export default function ArrivalsPage() {
    const { t } = useTranslation();
    // Stanice
    const [departureStation, setDepartureStation] = useState(null);
    const [arrivalStation, setArrivalStation] = useState(null);
    const { stations: depStations, loading: depLoading, input: depInput, setInput: setDepInput } = useStationSearch();
    const { stations: arrStations, loading: arrLoading, input: arrInput, setInput: setArrInput } = useStationSearch();

    // Pretraga
    const [journeyDate, setJourneyDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [tripResults, setTripResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searched, setSearched] = useState(false);

    // Filteri
    const [sortBy, setSortBy] = useState("time_asc");
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [timeRange, setTimeRange] = useState([0, 24]);
    const [directOnly, setDirectOnly] = useState(false);

    // Pretraga stanica sa debounce

    const handleSwap = () => {
        const tmp = departureStation;
        setDepartureStation(arrivalStation);
        setArrivalStation(tmp);
    };

    const handleSearch = useCallback(async (newDate) => {
        const requested = newDate || journeyDate;
        if (!departureStation || !arrivalStation || !requested) return;
        // Clamp na današnji datum — ne dozvoljavamo pretragu za prošli period
        // (npr. ako je stranica ostala otvorena preko ponoći).
        const today = dayjs().startOf("day");
        const dateToUse = dayjs(requested).isBefore(today, "day")
            ? today.format("YYYY-MM-DD")
            : requested;
        if (dateToUse !== journeyDate) setJourneyDate(dateToUse);
        setIsLoading(true);
        setError(null);
        setSearched(true);
        try {
            const results = await searchTripsApi({
                departureStationId: departureStation.id,
                arrivalStationId: arrivalStation.id,
                from: dateToUse,
            });
            setTripResults(results || []);
        } catch (err) {
            console.error(err);
            setError(t("homePage.searchError"));
            setTripResults([]);
        } finally {
            setIsLoading(false);
        }
    }, [departureStation, arrivalStation, journeyDate, t]);

    const handleDateChange = useCallback((d) => handleSearch(d), [handleSearch]);

    // Filtriranje i sortiranje
    const getTimeMinutes = useCallback((trip) => {
        const ts = trip.departureTime || trip.departureTimeStr || "";
        if (typeof ts === "string" && ts.match(/^\d{2}:\d{2}/)) { const [h, m] = ts.split(":").map(Number); return h * 60 + m; }
        try { const d = new Date(ts); if (!isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes(); } catch {}
        return 0;
    }, []);

    const availableCarriers = useMemo(() => {
        const map = new Map();
        tripResults.forEach((t) => {
            const name = t.operatorName || t.carrierName;
            const id = t.operatorId || t.carrierId;
            if (name && !map.has(name)) map.set(name, id);
        });
        return Array.from(map, ([name, id]) => ({ name, id }));
    }, [tripResults]);

    const filteredTrips = useMemo(() => {
        if (!tripResults.length) return [];
        const today = new Date();
        const todayStr = today.toISOString().split("T")[0];
        const isToday = journeyDate === todayStr;
        if (journeyDate < todayStr) return [];

        let result = [...tripResults];
        if (isToday) {
            const nowMin = today.getHours() * 60 + today.getMinutes() + 15;
            result = result.filter((t) => getTimeMinutes(t) > nowMin);
        }
        if (selectedCarrier) result = result.filter((t) => (t.operatorName || t.carrierName) === selectedCarrier.name);
        if (timeRange[0] > 0 || timeRange[1] < 24) {
            result = result.filter((t) => { const h = getTimeMinutes(t) / 60; return h >= timeRange[0] && h <= timeRange[1]; });
        }
        if (directOnly) result = result.filter((t) => !t.linkId);

        if (sortBy === "time_asc") result.sort((a, b) => getTimeMinutes(a) - getTimeMinutes(b));
        else if (sortBy === "time_desc") result.sort((a, b) => getTimeMinutes(b) - getTimeMinutes(a));
        else if (sortBy === "price_asc") result.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
        else if (sortBy === "price_desc") result.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
        return result;
    }, [tripResults, journeyDate, selectedCarrier, timeRange, directOnly, sortBy, getTimeMinutes]);

    const activeFilterCount = [selectedCarrier, timeRange[0] > 0 || timeRange[1] < 24, directOnly].filter(Boolean).length;
    const resetFilters = () => { setSortBy("time_asc"); setSelectedCarrier(null); setTimeRange([0, 24]); setDirectOnly(false); };

    const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "grey.50", height: 44 } };

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "#f5f6fa" }}>
            {/* Search Bar */}
            <Box sx={{ bgcolor: "white", borderBottom: "1px solid", borderColor: "grey.200", py: 2.5 }}>
                <Container maxWidth="lg">
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "flex-end" }}>
                        {/* Od */}
                        <Box sx={{ flex: 1, width: "100%" }}>
                            <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600, fontSize: "0.7rem", mb: 0.3, display: "block" }}>
                                {t("results.from")}
                            </Typography>
                            <Autocomplete
                                size="small"
                                options={depStations}
                                getOptionLabel={(o) => o?.name || ""}
                                value={departureStation}
                                onChange={(_, v) => setDepartureStation(v)}
                                onInputChange={(_, v, r) => { if (r === "input") setDepInput(v); }}
                                loading={depLoading}
                                filterOptions={(x) => x}
                                noOptionsText={depInput.length < 2 ? t("home.minTwoChars") : t("home.noStations")}
                                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder={t("home.from")} sx={inputSx}
                                        InputProps={{ ...params.InputProps,
                                            startAdornment: (<><Icon icon="mdi:crosshairs-gps" width={18} color="#637381" style={{ marginRight: 6 }} />{params.InputProps.startAdornment}</>),
                                            endAdornment: (<>{depLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        {/* Swap */}
                        <Stack alignItems="center" spacing={0.3} sx={{ pb: 0.2 }}>
                            <IconButton onClick={handleSwap} aria-label={t("common.swapStations")} sx={{ bgcolor: "primary.main", color: "white", borderRadius: 1.5, width: 44, height: 44, "&:hover": { bgcolor: "primary.dark", transform: "rotate(180deg)" }, transition: "all 0.3s" }}>
                                <Icon icon="mdi:swap-horizontal" width={20} />
                            </IconButton>
                        </Stack>

                        {/* Do */}
                        <Box sx={{ flex: 1, width: "100%" }}>
                            <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600, fontSize: "0.7rem", mb: 0.3, display: "block" }}>
                                {t("results.to")}
                            </Typography>
                            <Autocomplete
                                size="small"
                                options={arrStations}
                                getOptionLabel={(o) => o?.name || ""}
                                value={arrivalStation}
                                onChange={(_, v) => setArrivalStation(v)}
                                onInputChange={(_, v, r) => { if (r === "input") setArrInput(v); }}
                                loading={arrLoading}
                                filterOptions={(x) => x}
                                noOptionsText={arrInput.length < 2 ? t("home.minTwoChars") : t("home.noStations")}
                                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder={t("home.to")} sx={inputSx}
                                        InputProps={{ ...params.InputProps,
                                            startAdornment: (<><Icon icon="mdi:crosshairs-gps" width={18} color="#637381" style={{ marginRight: 6 }} />{params.InputProps.startAdornment}</>),
                                            endAdornment: (<>{arrLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        {/* Datum */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box sx={{ minWidth: 160 }}>
                                <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600, fontSize: "0.7rem", mb: 0.3, display: "block" }}>
                                    {t("arrivals.date")}
                                </Typography>
                                <DatePicker
                                    value={dayjs(journeyDate)}
                                    onChange={(v) => v && setJourneyDate(v.format("YYYY-MM-DD"))}
                                    format="DD.MM.YYYY"
                                    minDate={dayjs()}
                                    disablePast
                                    slotProps={{ textField: { size: "small", fullWidth: true, sx: inputSx } }}
                                />
                            </Box>
                        </LocalizationProvider>

                        {/* Pronadji */}
                        <Button
                            variant="contained"
                            onClick={() => handleSearch()}
                            disabled={!departureStation || !arrivalStation}
                            endIcon={<Icon icon="mdi:magnify" width={20} />}
                            sx={{ height: 44, px: 4, fontWeight: 700, fontSize: "0.95rem", borderRadius: 1.5, bgcolor: "#1a1a2e", whiteSpace: "nowrap", flexShrink: 0, "&:hover": { bgcolor: "#16213e" } }}
                        >
                            {t("results.find")}
                        </Button>
                    </Stack>
                </Container>
            </Box>

            {/* Date Strip */}
            {searched && journeyDate && (
                <Container maxWidth="lg" sx={{ mt: 3 }}>
                    <DateStrip selectedDate={journeyDate} onDateChange={handleDateChange} />
                </Container>
            )}

            {/* Filteri */}
            {!isLoading && tripResults.length > 0 && (
                <Container maxWidth="lg" sx={{ mt: 2 }}>
                    <Paper sx={{ px: 2.5, py: 1.5, borderRadius: 2, border: "1px solid", borderColor: "grey.200" }}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                            <TextField select size="small" value={sortBy} onChange={(e) => setSortBy(e.target.value)} label={t("results.sortBy")}
                                sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}>
                                <MenuItem value="time_asc">{t("results.sortTimeAsc")}</MenuItem>
                                <MenuItem value="time_desc">{t("results.sortTimeDesc")}</MenuItem>
                                <MenuItem value="price_asc">{t("results.sortPriceAsc")}</MenuItem>
                                <MenuItem value="price_desc">{t("results.sortPriceDesc")}</MenuItem>
                            </TextField>

                            {availableCarriers.length > 1 && (
                                <Autocomplete size="small" options={availableCarriers} getOptionLabel={(o) => o.name || ""}
                                    value={selectedCarrier} onChange={(_, v) => setSelectedCarrier(v)}
                                    isOptionEqualToValue={(o, v) => o.name === v.name}
                                    renderInput={(params) => <TextField {...params} label={t("results.carrier")} placeholder={t("results.allCarriers")} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} />}
                                    sx={{ minWidth: 200, flex: 1 }}
                                />
                            )}

                            <Button
                                variant={directOnly ? "contained" : "outlined"}
                                size="small"
                                onClick={() => setDirectOnly(!directOnly)}
                                startIcon={<Icon icon={directOnly ? "mdi:check" : "mdi:transit-connection-variant"} width={16} />}
                                sx={{
                                    height: 40, minWidth: 120, borderRadius: 1.5, fontWeight: 600, textTransform: "none",
                                    borderColor: directOnly ? "primary.main" : "grey.300", color: directOnly ? "white" : "text.primary",
                                    "&:hover": { borderColor: "primary.main" },
                                }}
                            >
                                {t("results.direct")}
                            </Button>

                            <TextField
                                select
                                size="small"
                                value={`${timeRange[0]}-${timeRange[1]}`}
                                onChange={(e) => {
                                    const [from, to] = e.target.value.split("-").map(Number);
                                    setTimeRange([from, to]);
                                }}
                                label={t("results.departureTimeFilter")}
                                sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                            >
                                <MenuItem value="0-24">{t("results.anyTime")}</MenuItem>
                                <MenuItem value="0-6">00:00 - 06:00</MenuItem>
                                <MenuItem value="6-10">06:00 - 10:00</MenuItem>
                                <MenuItem value="10-14">10:00 - 14:00</MenuItem>
                                <MenuItem value="14-18">14:00 - 18:00</MenuItem>
                                <MenuItem value="18-24">18:00 - 00:00</MenuItem>
                            </TextField>

                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip label={t("arrivals.tripsCount", { n: filteredTrips.length })} size="small" sx={{ fontWeight: 600, bgcolor: "grey.100" }} />
                                {activeFilterCount > 0 && (
                                    <Button size="small" onClick={resetFilters} startIcon={<Icon icon="mdi:filter-remove" width={16} />}
                                        sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "none" }}>{t("arrivals.reset")}</Button>
                                )}
                            </Stack>
                        </Stack>
                    </Paper>
                </Container>
            )}

            {/* Results */}
            <Container maxWidth="lg" sx={{ mt: 2, pb: 6 }}>
                {!searched ? (
                    <Paper sx={{ py: 8, textAlign: "center", borderRadius: 2 }}>
                        <Icon icon="mdi:bus-clock" width={48} color="#919EAB" />
                        <Typography variant="h6" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                            {t("arrivals.emptyTitle")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("arrivals.emptyDesc")}
                        </Typography>
                    </Paper>
                ) : isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TripResultsList
                        trips={filteredTrips}
                        isLoading={isLoading}
                        error={error}
                        onSelectTrip={() => {}}
                    />
                )}
            </Container>
        </Box>
    );
}
